import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { PermissionService } from '../services/permission-service';
import { AuthService } from '../services/auth.service';

@Injectable({ providedIn: 'root' })
export class PermissionGuard implements CanActivate, CanActivateChild {
    constructor(
        private permissionService: PermissionService,
        private authService: AuthService,
        private router: Router
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        // Get required permission(s) from route data. A single key or an array of
        // keys (matched with OR semantics, e.g. Approver Setup covering the two
        // permissions its tabs were split from) are both supported.
        const requiredPermission = route.data['permission'];

        // If no permission defined in route, allow access
        if (!requiredPermission) {
            return of(true);
        }

        const requiredPermissions: string[] = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];

        // Check if user has at least one of the required permissions
        return this.permissionService.getPermissionKeys$().pipe(
            take(1),
            map(keys => {
                if (requiredPermissions.some(key => keys.includes(key))) {
                    return true;
                }

                this.router.navigate([this.unauthorizedRoute()], {
                    queryParams: { from: state.url.split('?')[0] }
                });
                return false;
            }),
            catchError(err => {
                console.error('Permission guard error', err);
                this.router.navigate([this.unauthorizedRoute()]);
                return of(false);
            })
        );
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.canActivate(route, state);
    }

    // Admin and tenant shells each own an /unauthorized page so the denied user keeps
    // the layout (sidebar/header) matching the app they are signed into.
    private unauthorizedRoute(): string {
        return this.authService.isAdminUser() ? '/admin/unauthorized' : '/unauthorized';
    }
}
